import React, { useState } from 'react';
import {
  IonList,
  IonItem,
  IonLabel,
  IonBadge,
  IonButton,
  IonIcon,
  IonText,
  IonSpinner,
  IonCheckbox,
  IonAlert,
} from '@ionic/react';
import {
  refreshOutline,
  trashOutline,
  cloudDownloadOutline,
  alertCircleOutline,
  checkmarkCircleOutline,
  removeCircleOutline,
  helpCircleOutline,
} from 'ionicons/icons';
import { SyncComparisonResult, SyncStatus } from '@/types/model/sync';

interface SyncComparisonListProps {
  comparisons: SyncComparisonResult[];
  isLoading: boolean;
  onSelectInspection: (item: SyncComparisonResult) => void;
  onOverrideLocal: (ids: number[]) => Promise<void>;
  onDeleteLocal: (ids: number[]) => Promise<void>;
  onRefresh: () => void;
}

const statusConfig: Record<SyncStatus, { color: string; label: string; icon: string }> = {
  'in-sync': { color: 'success', label: 'In Sync', icon: checkmarkCircleOutline },
  'conflict': { color: 'danger', label: 'Conflict', icon: alertCircleOutline },
  'local-only': { color: 'medium', label: 'Local Only', icon: removeCircleOutline },
  'local-newer': { color: 'warning', label: 'Local Newer', icon: alertCircleOutline },
  'server-newer': { color: 'warning', label: 'Server Newer', icon: cloudDownloadOutline },
};

const SyncComparisonList: React.FC<SyncComparisonListProps> = ({
  comparisons,
  isLoading,
  onSelectInspection,
  onOverrideLocal,
  onDeleteLocal,
  onRefresh,
}) => {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);
  const [showOverrideAlert, setShowOverrideAlert] = useState(false);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);

  const toggleSelection = (id: number): void => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = (): void => {
    if (selectedIds.size === comparisons.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(comparisons.map(c => c.inspectionResultId)));
    }
  };

  const handleBulkOverride = async (): Promise<void> => {
    setIsBulkProcessing(true);
    try {
      await onOverrideLocal(Array.from(selectedIds));
      setSelectedIds(new Set());
    } finally {
      setIsBulkProcessing(false);
    }
  };

  const handleBulkDelete = async (): Promise<void> => {
    setIsBulkProcessing(true);
    try {
      await onDeleteLocal(Array.from(selectedIds));
      setSelectedIds(new Set());
    } finally {
      setIsBulkProcessing(false);
    }
  };

  const formatTimestamp = (ts: string | null): string => {
    if (!ts) return '—';
    try {
      return new Date(ts).toLocaleString();
    } catch {
      return ts;
    }
  };

  return (
    <div>
      {/* Header actions */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '8px',
        marginBottom: '12px',
      }}>
        <IonText style={{ fontWeight: 600, fontSize: '1.1rem' }}>
          Cached Inspections
        </IonText>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <IonButton
            size="small"
            fill="outline"
            onClick={onRefresh}
            disabled={isLoading || isBulkProcessing}
          >
            <IonIcon icon={refreshOutline} slot="start" />
            Refresh
          </IonButton>
          {comparisons.length > 0 && (
            <IonButton
              size="small"
              fill="outline"
              onClick={toggleSelectAll}
              disabled={isLoading || isBulkProcessing}
            >
              {selectedIds.size === comparisons.length ? 'Deselect All' : 'Select All'}
            </IonButton>
          )}
        </div>
      </div>

      {/* Bulk actions */}
      {selectedIds.size > 0 && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '12px',
          padding: '8px 12px',
          borderRadius: '8px',
          background: 'var(--ion-color-light)',
        }}>
          <IonText color="medium" style={{ fontSize: '0.85rem', flex: 1 }}>
            {selectedIds.size} selected
          </IonText>
          <IonButton
            size="small"
            fill="outline"
            color="warning"
            disabled={isBulkProcessing}
            onClick={() => setShowOverrideAlert(true)}
          >
            {isBulkProcessing ? <IonSpinner name="crescent" style={{ width: '16px', height: '16px' }} /> : (
              <IonIcon icon={cloudDownloadOutline} slot="start" />
            )}
            Override with Server
          </IonButton>
          <IonButton
            size="small"
            fill="outline"
            color="danger"
            disabled={isBulkProcessing}
            onClick={() => setShowDeleteAlert(true)}
          >
            {isBulkProcessing ? <IonSpinner name="crescent" style={{ width: '16px', height: '16px' }} /> : (
              <IonIcon icon={trashOutline} slot="start" />
            )}
            Delete Local
          </IonButton>
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div style={{ textAlign: 'center', padding: '32px' }}>
          <IonSpinner name="crescent" />
          <IonText color="medium" style={{ display: 'block', marginTop: '8px', fontSize: '0.85rem' }}>
            Comparing local and server data...
          </IonText>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && comparisons.length === 0 && (
        <div style={{ textAlign: 'center', padding: '32px' }}>
          <IonIcon icon={helpCircleOutline} color="medium" style={{ fontSize: '2.5rem' }} />
          <IonText color="medium" style={{ display: 'block', marginTop: '8px' }}>
            No cached inspections found. Visit the Inspecting page to cache data.
          </IonText>
        </div>
      )}

      {/* Comparison list */}
      {!isLoading && comparisons.length > 0 && (
        <IonList style={{ background: 'transparent' }}>
          {comparisons.map(item => {
            const config = statusConfig[item.status];
            return (
              <IonItem
                key={item.inspectionResultId}
                style={{ '--padding-start': '0px', cursor: 'pointer' }}
              >
                <IonCheckbox
                  slot="start"
                  checked={selectedIds.has(item.inspectionResultId)}
                  onIonChange={() => toggleSelection(item.inspectionResultId)}
                  style={{ marginRight: '12px' }}
                />
                <IonLabel
                  onClick={() => onSelectInspection(item)}
                  style={{ cursor: 'pointer' }}
                >
                  <h3 style={{ margin: 0, fontWeight: 500, fontSize: '0.9rem' }}>
                    {item.lineItem} — {item.lineItemTitle}
                  </h3>
                  <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: 'var(--ion-color-medium)' }}>
                    {item.groupTitle}
                  </p>
                  <div style={{ display: 'flex', gap: '16px', marginTop: '4px', fontSize: '0.75rem' }}>
                    <IonText color="medium">
                      Local: {formatTimestamp(item.localCachedAt)}
                    </IonText>
                    <IonText color="medium">
                      Server: {item.serverUpdatedAt ? formatTimestamp(item.serverUpdatedAt) : 'N/A'}
                    </IonText>
                  </div>
                </IonLabel>
                <div slot="end" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  {item.hasDifferences && item.checklistDiffs.length > 0 && (
                    <IonBadge color="warning" style={{ fontSize: '0.65rem', marginRight: '4px' }}>
                      {item.checklistDiffs.length}
                    </IonBadge>
                  )}
                  <IonBadge color={config.color} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <IonIcon icon={config.icon} style={{ fontSize: '0.8rem' }} />
                    {config.label}
                  </IonBadge>
                </div>
              </IonItem>
            );
          })}
        </IonList>
      )}

      {/* Alerts */}
      <IonAlert
        isOpen={showOverrideAlert}
        onDidDismiss={() => setShowOverrideAlert(false)}
        header="Override Local Data"
        message={`Replace ${selectedIds.size} local inspection(s) with server data? Local changes will be lost.`}
        buttons={[
          { text: 'Cancel', role: 'cancel' },
          { text: 'Override', handler: () => handleBulkOverride() },
        ]}
      />

      <IonAlert
        isOpen={showDeleteAlert}
        onDidDismiss={() => setShowDeleteAlert(false)}
        header="Delete Local Data"
        message={`Delete ${selectedIds.size} cached inspection(s)? This cannot be undone.`}
        buttons={[
          { text: 'Cancel', role: 'cancel' },
          { text: 'Delete', handler: () => handleBulkDelete() },
        ]}
      />
    </div>
  );
};

export default SyncComparisonList;
